import { ReactElement } from 'react';

import { styled } from '@mui/material';

import {
  Icons,
  Spinner,
  SpinnerProps,
  StatusChip,
  Box,
  Stack,
} from '@cast/design-system';
import { WorkloadRecommendationStatusTypeEnum } from '@cast/types';

import { CLICKABLE_STATUS_BADGE_COLOR_MAP } from './_constants';
import { WorkloadStatusChipTooltip } from './WorkloadStatusChipTooltip';
import { PreparedWorkload } from '../../../../../workload-optimization/types';
import { STATUS_DISPLAY_NAME_MAP } from '../../../_constants';

const Loader = styled(Spinner)<SpinnerProps>({
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'inherit !important',
  marginRight: 4,
});

type WorkloadStatusBadgeProps = {
  workload: PreparedWorkload;
  onAppliedStatusClick?(): void;
};

export const WorkloadStatusChip = ({
  workload,
  onAppliedStatusClick,
}: WorkloadStatusBadgeProps) => {
  const { id, recommendationStatus } = workload;
  const {
    appliedPods,
    totalPods,
    type: workloadStatus,
  } = recommendationStatus || {};

  let icon: ReactElement | null;

  switch (workloadStatus) {
    case WorkloadRecommendationStatusTypeEnum.APPLIED:
      icon =
        appliedPods < totalPods ? (
          <Loader
            testId="workload-status-chip-loader"
            thickness={4}
            disableShrink
            noBackground
          />
        ) : (
          <Stack sx={{ marginRight: 4, fontSize: 16 }}>
            <Icons.CheckCircle data-testid="workload-status-chip-checked-icon" />
          </Stack>
        );
      break;
    default:
      icon = null;
      break;
  }

  if (
    workloadStatus === WorkloadRecommendationStatusTypeEnum.WAITING ||
    workloadStatus === WorkloadRecommendationStatusTypeEnum.UNKNOWN
  ) {
    return null;
  }

  return (
    <WorkloadStatusChipTooltip
      workloadId={id}
      workloadStatus={workloadStatus}
      appliedPods={appliedPods}
      totalPods={totalPods}
    >
      <StatusChip
        testId="workload-status-chip"
        sx={CLICKABLE_STATUS_BADGE_COLOR_MAP[workloadStatus]}
        size="md"
        onClick={(e) => {
          e.stopPropagation();

          if (
            workloadStatus === WorkloadRecommendationStatusTypeEnum.APPLIED &&
            onAppliedStatusClick
          ) {
            onAppliedStatusClick();
          }
        }}
      >
        <Box display="flex" alignItems="center" whiteSpace="nowrap">
          {icon}
          {STATUS_DISPLAY_NAME_MAP[workloadStatus]}
          {workloadStatus === WorkloadRecommendationStatusTypeEnum.APPLIED &&
            ` ${appliedPods}/${totalPods}`}
        </Box>
      </StatusChip>
    </WorkloadStatusChipTooltip>
  );
};
